/**
 * Gravar o firmware do catálogo no robô pela USB (spec 2026-09-23 §5.6).
 *
 * Três regras que não são detalhe:
 *
 * 1. **Nada toca a porta antes do binário conferir.** Tamanho e `sha256`
 *    contra o manifesto primeiro; binário errado nunca chega à placa.
 * 2. **Só três regiões, sempre as mesmas.** A tabela de partições em `0x8000`,
 *    `0xFF` em `otadata` (`0xe000`), que faz o bootloader voltar a `app0` mesmo
 *    num robô que já recebeu OTA e roda de `app1`, e o app em `0x10000`. A tabela
 *    é a da versão que se grava (spec 2026-09-30-robo-tabela-min-spiffs) e só
 *    entra se mantiver a NVS e o otadata no lugar. NVS (Wi-Fi e credencial) e
 *    bootloader NUNCA são escritos: o robô volta configurado.
 * 3. **Placa certa.** O MAC lido pela ROM tem de ser o do cadastro (quando o
 *    cadastro já tem um), e o firmware que ela roda, se responder `INFO`, tem
 *    de ser do mesmo modelo que o manifesto. Gravar o display errado dá um
 *    robô que liga e não mostra nada.
 *
 * Sem React e sem porta: tudo chega por `Dependencias`, e o teste dirige com
 * falsos.
 */
import { erroEmTexto } from './diagnostico'
import type { Carregador, Regiao } from './esptool'
import { identificarRobo, PRAZOS_PADRAO, type Prazos } from './fluxo'
import type { InfoDoRobo } from './protocolo'
import type { Linhas } from './serial'

export const OFFSET_TABELA = 0x8000
/** A tabela ocupa um setor; a NVS começa em `0x9000` e nunca é escrita. */
export const TAMANHO_MAX_TABELA = 0x1000
export const OTADATA = 0xe000
export const TAMANHO_OTADATA = 0x2000
export const OFFSET_APP = 0x10000
/**
 * As fatias `app0` das duas tabelas da frota, `default` e `min_spiffs`. É a
 * mesma tupla de `scripts/robo/build_firmware.py` (`FATIAS_CONHECIDAS`).
 */
export const FATIAS_CONHECIDAS: readonly number[] = [0x140000, 0x1e0000]
const NVS = { offset: 0x9000, tamanho: 0x5000 }

export type Manifesto = {
  versao: string
  perfil: string
  sha256: string
  tamanho: number
  offset_app: number
  fatia_app: number
  sha256_particoes: string
}

export type PassoGravacao =
  | 'baixar' | 'identificar' | 'conectar' | 'gravar' | 'reiniciar' | 'confirmar'

export type EventoGravacao =
  | { tipo: 'passo'; passo: PassoGravacao }
  | { tipo: 'progresso'; feitos: number; total: number }

export type ResultadoGravacao =
  | { ok: true; mac: string; versao: string }
  | { ok: false; passo: PassoGravacao; motivo: string; mac: string | null }

export type Dependencias = {
  baixarApp: (versao: string) => Promise<Uint8Array>
  baixarParticoes: (versao: string) => Promise<Uint8Array>
  sha256: (dados: Uint8Array) => Promise<string>
  abrirLinhas: () => Promise<Linhas>
  abrirCarregador: () => Promise<Carregador>
  aoAvancar: (e: EventoGravacao) => void
  /**
   * O motivo, se a leitura da porta caiu (`leituraPerdida.ts`). Quando cai, o
   * esptool-js só diz "Failed to connect", igual à placa que pede BOOT.
   */
  motivoDaQueda?: () => string | null
  agora?: () => number
  esperar?: (ms: number) => Promise<void>
  prazos?: Prazos
}

/** O que o cadastro diz desta placa. `mac` nulo = primeira gravação. */
export type Esperado = { perfil: string; mac: string | null }

type Particao = { tipo: number; subtipo: number; offset: number; tamanho: number }

/** As entradas da tabela binária do ESP32: 32 bytes cada, magia `0x50AA`. */
function lerTabela(tabela: Uint8Array): Particao[] {
  const v = new DataView(tabela.buffer, tabela.byteOffset, tabela.byteLength)
  const saida: Particao[] = []
  for (let i = 0; i + 32 <= tabela.length; i += 32) {
    if (v.getUint16(i, true) !== 0x50aa) break
    saida.push({
      tipo: v.getUint8(i + 2), subtipo: v.getUint8(i + 3),
      offset: v.getUint32(i + 4, true), tamanho: v.getUint32(i + 8, true),
    })
  }
  return saida
}

/**
 * A mesma guarda do build (`conferir_tabela`): NVS e otadata no lugar, e `app0`
 * com a fatia do manifesto. Uma tabela que mova a NVS apagaria o Wi-Fi e o
 * token do robô na primeira gravação.
 */
export function tabelaCompativel(tabela: Uint8Array, fatia: number): boolean {
  if (tabela.length === 0 || tabela.length > TAMANHO_MAX_TABELA) return false
  if (!FATIAS_CONHECIDAS.includes(fatia)) return false
  const p = lerTabela(tabela)
  const uma = (tipo: number, subtipo: number) => p.find((x) => x.tipo === tipo && x.subtipo === subtipo)
  const nvs = uma(1, 0x02)
  const ota = uma(1, 0x00)
  return nvs?.offset === NVS.offset && nvs.tamanho === NVS.tamanho
    && ota?.offset === OTADATA && ota.tamanho === TAMANHO_OTADATA
    && p.some((x) => x.tipo === 0 && x.offset === OFFSET_APP && x.tamanho === fatia)
}

/** As três regiões, na ordem. Recusa app que não cabe na fatia do manifesto. */
export function regioesDaGravacao(app: Uint8Array, tabela: Uint8Array, fatia: number): Regiao[] {
  if (app.length === 0 || app.length > fatia) {
    throw new Error(`app com ${app.length} bytes não cabe na fatia de ${fatia}`)
  }
  return [
    { endereco: OFFSET_TABELA, dados: tabela },
    { endereco: OTADATA, dados: new Uint8Array(TAMANHO_OTADATA).fill(0xff) },
    { endereco: OFFSET_APP, dados: app },
  ]
}

/** A tabela até um setor, o otadata inteiro e o app dentro de `app0`. Nada mais. */
export function regiaoPermitida(r: Regiao, fatia: number): boolean {
  if (r.endereco === OFFSET_TABELA) return r.dados.length > 0 && r.dados.length <= TAMANHO_MAX_TABELA
  if (r.endereco === OTADATA) return r.dados.length === TAMANHO_OTADATA
  return r.endereco >= OFFSET_APP && r.endereco + r.dados.length <= OFFSET_APP + fatia
}

const esperarReal = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Passo 6: quantas vezes perguntar INFO depois da gravação, e a pausa entre elas. */
const TENTATIVAS_DE_CONFIRMAR = 2
const ESPERA_ENTRE_TENTATIVAS_MS = 1500

async function perguntarInfo(deps: Dependencias): Promise<{ info: InfoDoRobo | null } | undefined> {
  const linhas = await deps.abrirLinhas()
  try {
    return await identificarRobo(linhas, deps.prazos ?? PRAZOS_PADRAO,
      deps.agora ?? Date.now, deps.esperar ?? esperarReal)
  } finally {
    await linhas.fechar()
  }
}

export async function gravarFirmware(
  deps: Dependencias, manifesto: Manifesto, esperado: Esperado,
): Promise<ResultadoGravacao> {
  const { aoAvancar } = deps
  let mac: string | null = null
  const falha = (passo: PassoGravacao, motivo: string): ResultadoGravacao =>
    ({ ok: false, passo, motivo, mac })

  // 1. Baixar e conferir — regra 1.
  aoAvancar({ tipo: 'passo', passo: 'baixar' })
  if (manifesto.perfil !== esperado.perfil || manifesto.offset_app !== OFFSET_APP) {
    return falha('baixar', 'O catálogo devolveu firmware de outro modelo. Nada foi gravado.')
  }
  let app: Uint8Array
  let tabela: Uint8Array
  try {
    app = await deps.baixarApp(manifesto.versao)
    tabela = await deps.baixarParticoes(manifesto.versao)
  } catch {
    return falha('baixar', 'Não foi possível baixar o firmware. Nada foi gravado.')
  }
  if (app.length !== manifesto.tamanho || (await deps.sha256(app)) !== manifesto.sha256
      || (await deps.sha256(tabela)) !== manifesto.sha256_particoes
      || !tabelaCompativel(tabela, manifesto.fatia_app)) {
    return falha('baixar', 'O arquivo baixado não confere com o catálogo. Nada foi gravado.')
  }
  let regioes: Regiao[]
  try {
    regioes = regioesDaGravacao(app, tabela, manifesto.fatia_app)
  } catch {
    return falha('baixar', 'O firmware não cabe na partição desta versão. Nada foi gravado.')
  }
  if (!regioes.every((r) => regiaoPermitida(r, manifesto.fatia_app))) {
    return falha('baixar', 'Região de gravação fora do permitido. Nada foi gravado.')
  }

  // 2. Que firmware a placa roda? Placa virgem não responde, e segue.
  aoAvancar({ tipo: 'passo', passo: 'identificar' })
  const antes = await perguntarInfo(deps)
  if (antes?.info && antes.info.perfil !== manifesto.perfil) {
    return falha('identificar',
      `Este robô roda firmware de outro modelo (${antes.info.perfil}); o cadastro diz ${manifesto.perfil}. `
      + 'Confira o modelo no cadastro antes de gravar. Nada foi gravado.')
  }

  // 3. Conectar pela ROM e conferir a placa — regra 3.
  aoAvancar({ tipo: 'passo', passo: 'conectar' })
  let carregador: Carregador
  try {
    carregador = await deps.abrirCarregador()
  } catch (erro) {
    return falha('conectar', deps.motivoDaQueda?.()
      ?? 'Não consegui entrar no modo de gravação. Segure o botão BOOT da placa ao clicar e tente de novo.'
        + ` (a gravação respondeu ${erroEmTexto(erro)})`)
  }
  try {
    if (carregador.chip() !== 'ESP32') {
      return falha('conectar', `A placa conectada é ${carregador.chip()}, não um ESP32. Nada foi gravado.`)
    }
    mac = await carregador.mac()
    if (esperado.mac && mac !== esperado.mac.toLowerCase()) {
      return falha('conectar',
        `Esta placa (${mac}) não é o robô cadastrado (${esperado.mac}). Nada foi gravado.`)
    }

    // 4. Gravar e verificar.
    aoAvancar({ tipo: 'passo', passo: 'gravar' })
    try {
      await carregador.gravar(regioes, (feitos, total) =>
        aoAvancar({ tipo: 'progresso', feitos, total }))
    } catch (erro) {
      const queda = deps.motivoDaQueda?.()
      return falha('gravar',
        'A gravação parou no meio. O robô fica sem firmware até gravar de novo — não o desligue; tente de novo. '
        + (queda ?? `(a gravação respondeu ${erroEmTexto(erro)})`))
    }

    // 5. Reiniciar no firmware novo.
    aoAvancar({ tipo: 'passo', passo: 'reiniciar' })
    await carregador.reiniciar()
  } finally {
    await carregador.fechar().catch(() => {})
  }

  // 6. Confirmar pela versão que o próprio robô informa. A porta acabou de ser
  // solta pelo carregador e a placa acabou de reiniciar: a primeira reabertura
  // pode estourar ou chegar calada, com a gravação boa (TASK-11864). Robô que
  // RESPONDE com outra versão não ganha segunda chance.
  aoAvancar({ tipo: 'passo', passo: 'confirmar' })
  let depois: { info: InfoDoRobo | null } | undefined
  for (let tentativa = 0; tentativa < TENTATIVAS_DE_CONFIRMAR && depois === undefined; tentativa++) {
    if (tentativa > 0) await (deps.esperar ?? esperarReal)(ESPERA_ENTRE_TENTATIVAS_MS)
    depois = await perguntarInfo(deps).catch(() => undefined)
  }
  if (depois?.info?.versao !== manifesto.versao) {
    const queda = depois === undefined ? deps.motivoDaQueda?.() : null
    return falha('confirmar', queda
      ? `O firmware foi gravado e conferido, mas a conexão USB caiu antes de o robô informar a versão. ${queda}`
      : 'A gravação terminou, mas o robô não confirmou a versão nova. Desconecte e conecte o cabo e confira na página.')
  }
  // `mac` foi lido no passo 3: sem ele, a função já teria voltado.
  return { ok: true, mac: mac as string, versao: manifesto.versao }
}
